import { DeviceARPDataType, DeviceSwitchPortsDataType } from '../types/buildingTypes';
import { MacMiniValidationResult } from '../types/types';
import { formatMacAddress } from './macFormatter';

/** Matches "Print Server"/"Print-Server"/"PrintServer"/"cups" port descriptions. */
export const MAC_MINI_PORT_DESCRIPTION_REGEX = /print[\s-]?server|cups/i;

/** Reserved IP of the Mac Mini's primary/management Ethernet NIC. */
export const MAC_MINI_PRIMARY_IP = '10.255.245.5';

/** Maximum number of Mac Mini ports expected per switch (primary + secondary NIC). */
export const MAC_MINI_MAX_PORTS = 2;

/** True if the port description identifies it as a Mac Mini port. */
export function isMacMiniPort(row: Pick<DeviceSwitchPortsDataType, 'description'>): boolean {
  return MAC_MINI_PORT_DESCRIPTION_REGEX.test(row.description || '');
}

/**
 * Normalizes a port's `mac_address` field into a flat list of formatted MAC
 * strings. The field is typed as `string` but the API may return a single
 * address, an array, or a map of addresses; all shapes are handled here.
 */
function getPortMacAddresses(mac_address: unknown): string[] {
  if (!mac_address) return [];

  const values =
    Array.isArray(mac_address) || typeof mac_address === 'string'
      ? mac_address
      : Object.values(mac_address as Record<string, string>);

  return ([] as string[])
    .concat(values as string | string[])
    .filter(Boolean)
    .map((mac) => formatMacAddress(mac));
}

/** Builds a lookup of normalized MAC address to its ARP record. */
function buildArpIndex(arpData: DeviceARPDataType[]): Map<string, DeviceARPDataType> {
  const index = new Map<string, DeviceARPDataType>();
  (arpData || []).forEach((entry) => {
    if (entry.mac_address) index.set(formatMacAddress(entry.mac_address), entry);
  });
  return index;
}

/**
 * Validates a Mac Mini Flip/Bounce target port using ARP data.
 *
 * Eligibility requires: the port description identifies a Mac Mini port;
 * at most {@link MAC_MINI_MAX_PORTS} such ports exist on the switch; every
 * one of them has a MAC address (otherwise the whole group is blocked);
 * the port's MAC resolves to an ARP record; and that record's IP is not
 * the primary NIC ({@link MAC_MINI_PRIMARY_IP}).
 */
export function validateMacMiniPortAction(
  row: DeviceSwitchPortsDataType,
  allPorts: DeviceSwitchPortsDataType[],
  arpData: DeviceARPDataType[]
): MacMiniValidationResult {
  if (!isMacMiniPort(row)) {
    return {
      ok: false,
      reason: 'Flip/bounce is only allowed for Mac Mini ports (description containing \'Print Server\' or \'cups\').',
    };
  }

  const macMiniPorts = (allPorts || []).filter(isMacMiniPort);

  if (macMiniPorts.length > MAC_MINI_MAX_PORTS) {
    return {
      ok: false,
      reason: `Flip/bounce is blocked: this switch has ${macMiniPorts.length} Mac Mini ports, but only ${MAC_MINI_MAX_PORTS} are expected (one primary, one secondary NIC). Please try again later or contact the network team.`,
    };
  }

  const hasPortWithoutMac = macMiniPorts.some(
    (port) => getPortMacAddresses(port.mac_address).length === 0
  );

  if (hasPortWithoutMac) {
    return {
      ok: false,
      reason:
        'Flip/bounce is blocked: at least one Mac Mini port on this switch has no MAC address, so none of them can be verified as primary or secondary.',
    };
  }

  const arpIndex = buildArpIndex(arpData);
  const macs = getPortMacAddresses(row.mac_address);
  const arpRecords = macs
    .map((mac) => arpIndex.get(mac))
    .filter((record): record is DeviceARPDataType => Boolean(record));

  if (arpRecords.length === 0) {
    return {
      ok: false,
      reason: `Flip/bounce is blocked: port '${row.port}' has no matching ARP record, so its IP address (and therefore whether it is primary or secondary) cannot be verified.`,
    };
  }

  const touchesPrimaryNic = arpRecords.some((record) => record.ip_address === MAC_MINI_PRIMARY_IP);

  if (touchesPrimaryNic) {
    return {
      ok: false,
      reason: `Flip/bounce is blocked: port '${row.port}' is the Mac Mini's primary Ethernet connection (IP ${MAC_MINI_PRIMARY_IP}).`,
    };
  }

  return { ok: true };
}
